import { Box, Container } from "@mui/material";
import { ChartHeader } from "./ChartHeader";
import loaderGif from '../../../assets/Images/loader.svg';
import React, {  Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import { getGraphList } from "../../../redux/action";
import constant from "../../../constants/constant";
import { initialKeyMappings, outcomeKeyMappings, outcomeOptions, reasonOptions, sourceOptions } from "./Options";
import { filterData, transformAvgMonths, transformInitially, transformMonths, transformOutcome, transformRedFlagCount, transformRedFlagTriageCount, transformRejectedConditions, transformSource } from "./TransformFun";
import { toast } from "react-toastify";
import moment from "moment";
const BarChartCard = React.lazy(() =>
  import('./BarChartCard').then((module) => ({
    default: module.BarChartCard,
  }))
);
const StackedChartCard = React.lazy(() =>
  import('./StackedChartCard').then((module) => ({
    default: module.StackedChartCard,
  }))
);


const selectGraphData = createSelector(
  (state: any) => state?.userManagementReducer,
  (state) => state.graphData
);

const graphDateRange = createSelector(
  (state: any) => state?.userManagementReducer,
  (state) => state.customDatePick
);

interface OptionType {
  label: string;
  value: string;
}

const ReportCharts = () => {
  const lastFetchedMonthRef = useRef<any>(null);
  const chartRefs = useRef<any>([]);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>();
  const isGenerating = useRef(false);
  const [name,setName] = useState('');
  const [month,setMonth] = useState('12');
  const [trigger, setTrigger] = useState(0);
  const [localMonth,setLocalMonth] = useState(month);
  const [openDatePicker, setOpenDatePicker] = useState<{ [key: number]: boolean }>({});
  const [selectedFilter, setSelectedFilter] = useState<OptionType[]>([]);
  const [selectedfilteredOutcomeDataFilter, setSelectedfilteredOutcomeDataFilter] = useState<OptionType[]>([]);
  const [selectedfilteredInitialDataFilter, setSelectedfilteredInitialDataFilter] = useState<OptionType[]>([]);
  const [filteredOutcomeData, setFilteredOutcomeData] = useState<any>([]);
  const [filteredInitialData, setFilteredInitialData] = useState<any>([]);
  const graphData = useSelector(selectGraphData);
  const customDatePick = useSelector(graphDateRange);
  const [graphLocalData, setGraphData] = useState(graphData);
  const { activation_rates, averageForCaseloadActivity,rejectConditions, source,redFlagCount,redFlagTriageAcceptedRejectedCount,initiallyAcceptedAndRejectedCount } = graphLocalData || {};
  const { parent, school } = activation_rates || {};
  const { avgParentDays,avgEducationDays,avgReadyToClinicianDays,avgReadyToClinicianToCloseDays,avgClosedDays,percentageOfReferralsMdtAccepted,percentageOfReferralsMdtRejected } = averageForCaseloadActivity || {};
  const transformedParentData = useMemo(() => transformMonths(parent), [parent]);
  const transformedSchoolData = useMemo(() => transformMonths(school), [school]);
  const transformedAvgParentData = useMemo(() => transformAvgMonths(avgParentDays), [avgParentDays]);
  const transformedAvgEducationData = useMemo(() => transformAvgMonths(avgEducationDays), [avgEducationDays]);
  const transformedAvgClinicianData = useMemo(() => transformAvgMonths(avgReadyToClinicianDays), [avgReadyToClinicianDays]);
  const transformedAvgClinicianClosedData = useMemo(() => transformAvgMonths(avgReadyToClinicianToCloseDays), [avgReadyToClinicianToCloseDays]);
  const transformedAvgClosedData = useMemo(() => transformAvgMonths(avgClosedDays), [avgClosedDays]);
  const transformedSourceData = useMemo(() => transformSource(source), [source]);
  const transformedRedFlagData = useMemo(() => transformRedFlagCount(redFlagCount), [redFlagCount]);
  const transformedRedFlagTriageAcceptedRejectedCountData = useMemo(() => transformRedFlagTriageCount(redFlagTriageAcceptedRejectedCount), [redFlagTriageAcceptedRejectedCount]);
  const transformedOutcomeData = useMemo(() => transformOutcome(percentageOfReferralsMdtAccepted,percentageOfReferralsMdtRejected), [percentageOfReferralsMdtAccepted,percentageOfReferralsMdtRejected]);
  const transformedRejectConditions = useMemo(() => transformRejectedConditions(rejectConditions, constant.CLINICAL_REVIEW),[rejectConditions]);
  const transformedinitiallyAcceptedAndRejectedCountData = useMemo(() => transformInitially(initiallyAcceptedAndRejectedCount), [initiallyAcceptedAndRejectedCount]);

  useEffect(() => {
    if (!graphData) return;
    if (graphData) { 
      setGraphData((prevData: any) => ({
        ...prevData,
        ...graphData,
        activation_rates: {
          ...prevData.activation_rates,
          ...graphData.activation_rates,
        },
        averageForCaseloadActivity: {
          ...prevData.averageForCaseloadActivity,
          ...graphData.averageForCaseloadActivity,
        },
      }));
    }
     // eslint-disable-next-line
  }, [graphData]);
  

  const fetchGraphData = (monthValue: string | number) => {
    if ((lastFetchedMonthRef.current === monthValue || monthValue === 'CUSTOM') && !name) return;
    lastFetchedMonthRef.current = monthValue;
    const isSpecialFilter = [constant.FILTERS_NAME.source].includes(name);
  
    const payloadRange: any = {
      startDate: moment(customDatePick.start).format('DD/MM/YYYY'),
      endDate: moment(customDatePick.end).format('DD/MM/YYYY'),
      ...(customDatePick.name !== 'header' && customDatePick.name ? { name: customDatePick.name } : {})
    };
  
    let payload: any = {
      result1Filter: isSpecialFilter ? [] : selectedFilter,
      ...(name && { name }),
      ...(isSpecialFilter && { status: selectedFilter }),
    };
  
    if (customDatePick.start && customDatePick.end) {
      payload = { ...payload, ...payloadRange };
    } else {
      payload = { ...payload, month: monthValue };
    }
  
    if (monthValue !== '' || (customDatePick.start && customDatePick.end)) {
      dispatch(getGraphList(payload));
    }
  };

  useEffect(() => {
    if (!graphData) return;
    if (graphData) { 
      setGraphData((prevData: any) => ({
        ...prevData,
        ...graphData,
        activation_rates: {
          ...prevData.activation_rates,
          ...graphData.activation_rates,
        },
        averageForCaseloadActivity: {
          ...prevData.averageForCaseloadActivity,
          ...graphData.averageForCaseloadActivity,
        },
      }));
    }
     // eslint-disable-next-line
  }, [graphData]);
  
  useEffect(() => {
    fetchGraphData(month);
    // eslint-disable-next-line 
  }, [dispatch, month, name, selectedFilter,customDatePick]);
  
  useEffect(() => {
    fetchGraphData(localMonth);
    lastFetchedMonthRef.current = ''
    // eslint-disable-next-line 
  }, [localMonth, trigger]);

  const rejectionLabels = Object.values(constant.CLINICAL_REVIEW);  


  useEffect(() => {
    const outcomeSelectedKeys:any = selectedfilteredOutcomeDataFilter.length > 0
      ? selectedfilteredOutcomeDataFilter.filter((filter:any) => filter in outcomeKeyMappings)
      : [];
    const filteredOutcomeData:any = filterData(transformedOutcomeData, outcomeSelectedKeys, outcomeKeyMappings);
    setFilteredOutcomeData(filteredOutcomeData);
    const initialSelectedKeys:any = selectedfilteredInitialDataFilter.length > 0
      ? selectedfilteredInitialDataFilter.filter((filter:any) => filter in initialKeyMappings)
      : [];

    const filteredInitialData = filterData(transformedinitiallyAcceptedAndRejectedCountData, initialSelectedKeys, initialKeyMappings);
    setFilteredInitialData(filteredInitialData);
    // eslint-disable-next-line 
  }, [selectedfilteredInitialDataFilter, selectedfilteredOutcomeDataFilter, transformedOutcomeData, transformedinitiallyAcceptedAndRejectedCountData]);

  const chartConfigs = [
    { title: "Account Activation Rate By Parents (%)",openDatePicker,setOpenDatePicker, data: transformedParentData, color: "#7B9DFF",setMonth,month,name:constant.FILTERS_NAME.activationRatesParent,setName },
    { title: "Account Activation Rate By Schools (%)",openDatePicker,setOpenDatePicker, data: transformedSchoolData, color: "#8C44F7",setMonth,month,name:constant.FILTERS_NAME.activationRatesSchool,setName },
    { title: "Average time of parent report submission",openDatePicker,setOpenDatePicker, data: transformedAvgParentData, color: "#6A2382",setMonth,month,name:constant.FILTERS_NAME.avgParentDays,setName },
    { title: "Average time of Education report submission",openDatePicker,setOpenDatePicker, data: transformedAvgEducationData, color: "#99D7CA",setMonth,month,name:constant.FILTERS_NAME.avgEducationDays,setName },
    { title: "Average time to gather all required referral details",openDatePicker,setOpenDatePicker, data: transformedAvgClinicianData, color: "#4A3AFF",setMonth,month,name:constant.FILTERS_NAME.avgReadyToClinicianDays,setName },
    { title: "Average time taken to review completed referrals",openDatePicker,setOpenDatePicker, data: transformedAvgClinicianClosedData, color: "#962DFF",setMonth,month,name:constant.FILTERS_NAME.avgReadyToClinicianToCloseDays,setName },
    { title: "Average time from referral receipt to Initial recording",openDatePicker,setOpenDatePicker, data: transformedAvgClosedData, color: "#C57D51",setMonth,month,name:constant.FILTERS_NAME.avgClosedDays,setName },
  ];
  const stackChartConfigs = [
    { title: "Number of Red Flag Triage Accepted Rejected Count",openDatePicker,setOpenDatePicker, data: transformedRedFlagTriageAcceptedRejectedCountData,keys:["moved_to_active", "moved_to_reject","total_redflag_referrals"], colors: ["#962DFF", "#E0C6FD","#93AAFD"],setMonth,month,name:constant.FILTERS_NAME.redFlagTriageAcceptedRejectedCount,setName,selectedFilter, setSelectedFilter},
    { title: "Number of Red Flag Referrals",openDatePicker,setOpenDatePicker, data: transformedRedFlagData,keys:["redflag_count", "total_count"], colors: ["#962DFF", "#E0C6FD"],setMonth,month,name:constant.FILTERS_NAME.redFlagCount,setName,selectedFilter, setSelectedFilter},
    { title: "Number of referrals from different source",openDatePicker,setOpenDatePicker, data: transformedSourceData,keys:["admin", "openUrl", "school"], colors: ["#962DFF", "#E0C6FD", "#93AAFD"],options:sourceOptions,setMonth,month,name:constant.FILTERS_NAME.source,setName,selectedFilter, setSelectedFilter},
    { title: "Number of outcome accepted or rejected for further assessment",openDatePicker,setOpenDatePicker, data: filteredOutcomeData,keys:["accept", "reject"], colors: ["#6A2382","#E0C6FD"],options:outcomeOptions,setMonth,month,name:constant.FILTERS_NAME.percentageOfReferralsMdtAcceptedOrRejected,setName,selectedfilteredOutcomeDataFilter, setSelectedfilteredOutcomeDataFilter},
    { title: "Referrals initially accepted/rejected (in Days / Percentage)",openDatePicker,setOpenDatePicker, data: filteredInitialData,keys:["accepted_percentage", "rejected_percentage"], colors: ["#E0C6FD", "#962DFF", "#93AAFD"],options:outcomeOptions,setMonth,month,name:constant.FILTERS_NAME.initiallyAcceptedAndRejectedCount,setName,selectedfilteredInitialDataFilter, setSelectedfilteredInitialDataFilter},
    { title: "Number of rejections recorded against each listed reason for rejection",openDatePicker,setOpenDatePicker, data: transformedRejectConditions,keys: rejectionLabels,selectedFilter, setSelectedFilter, 
      colors: ["#6A2382", "#E0C6FD", "#962DFF", "#93AAFD", "#4A3AFF", "#99D7CA", "#8C44F7", "#7B9DFF", "#C57D51"],options:reasonOptions,setMonth,month,name:constant.FILTERS_NAME.rejectConditions,setName 
    },  
  ];

  const handleOptionSelection = (value:any,name:any) => {
    if(name === constant.FILTERS_NAME.percentageOfReferralsMdtAcceptedOrRejected){
      setSelectedfilteredOutcomeDataFilter(value)
    }else if(name === constant.FILTERS_NAME.initiallyAcceptedAndRejectedCount){
      setSelectedfilteredInitialDataFilter(value)
    }else(
      setSelectedFilter(value)
    )
  }

  useEffect(() => {
    if (!chartRefs.current?.length || isGenerating.current) return;
    isGenerating.current = true;
     // eslint-disable-next-line
  }, [chartRefs]);


  const generatePDF = async () => {
    setIsLoading(true);
    isGenerating.current = true;
  
    try {
      const pdf = new (await import("jspdf")).default('p', 'mm', 'a4');
      const validRefs = chartRefs.current.filter((ref: any) => ref);
      let yOffset = 10;
      const pageHeight = pdf.internal.pageSize.height - 10;
  
      if (validRefs.length === 0) {
        toast.error('No charts found.');
        return;
      }
  
      for (let i = 0; i < validRefs.length; i++) {
        const ref = validRefs[i];
        if (!ref) continue;
  
        try {
          // console.log(`Capturing chart ${i + 1}...`);
          await new Promise((resolve) => setTimeout(resolve, 900));
  
          const canvas = await (await import("html2canvas")).default(ref, {
            backgroundColor: '#fff',
            scale: 2,
            useCORS: true,
          });
  
          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          const imgWidth = 180;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
          if (yOffset + imgHeight > pageHeight) {
            pdf.addPage();
            yOffset = 10;
          }
  
          pdf.addImage(imgData, 'JPEG', 15, yOffset, imgWidth, imgHeight);
          yOffset += imgHeight + 5;
        } catch (error) {
          console.error(`Error capturing chart ${i + 1}:`, error);
        }
      }
  
      const pdfBlobUrl:any = pdf.output('bloburl');
      if (!pdfBlobUrl) {
        console.error('PDF generation failed');
        toast.error('Failed to generate PDF.');
        return;
      }
  
      toast.success('Charts are ready for download!');
  
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = 'charts.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating charts PDF:', error);
      toast.error('Failed to generate PDF.');
    } finally {
      isGenerating.current = false;
      setIsLoading(false);
    }
  };
  

  const handleDataFromChild = (childValue:any) => {
    setTrigger((prev) => prev + 1);
    setLocalMonth(childValue)
  };

  const handleClearLocalState = (val: string) => val === 'CLEAR' && setLocalMonth('');

  const handleOpenDatePicker = (index: number) => {
    setOpenDatePicker((prev) => ({
      [index]: !prev[index], 
    }));
  };

  return (
    <Box fontFamily={"poppins"}>
      <ChartHeader sendClearLocalState={handleClearLocalState}  excelOption={true} handleClick={generatePDF} setMonth={setMonth} month={month} name={name} setName={setName} openDatePicker={openDatePicker[15] || false}   setOpenDatePicker={() => handleOpenDatePicker(15)}/>
   
      <Container maxWidth="xl" sx={{p:{xs:1,sm:1,md:0,lg:0,xl:0},
      width:{
        xl:'1500px',
        md:'900px',
        lg:'1200px',
        sm:'100%',
        xs:'100%',
      },
      margin:'auto'}}>

    <Suspense fallback={<div className='loadergif'><img src={loaderGif} alt='loader' /> </div>}>
      {chartConfigs.map(({ title, data, color, month,name,setName,openDatePicker,setOpenDatePicker,setMonth }, index) => (
        <div ref={(el:any) => (chartRefs.current[index] = el)} key={index} >
          <BarChartCard sendDataToParent={handleDataFromChild} setMonth={setMonth}  title={title} data={data} color={color}  month={month} name={name} setName={setName} openDatePicker={openDatePicker[index] || false}   setOpenDatePicker={() => handleOpenDatePicker(index)}/>
        </div>
      ))}
      {stackChartConfigs.map(({ title, data, keys,colors,options,month,name,setName,selectedFilter, setSelectedFilter,selectedfilteredInitialDataFilter, setSelectedfilteredInitialDataFilter,selectedfilteredOutcomeDataFilter, setSelectedfilteredOutcomeDataFilter, openDatePicker,setOpenDatePicker }, index) => (
        <div ref={(el:any) => (chartRefs.current[chartConfigs.length + index] = el)} key={chartConfigs.length + index}  >
          <StackedChartCard sendDataToParent={handleDataFromChild} handleOptionSelection={handleOptionSelection} options={options}  key={index} title={title} data={data} keys={keys} colors={colors}  month={month} name={name} setName={setName} selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} selectedfilteredInitialDataFilter={selectedfilteredInitialDataFilter} setSelectedfilteredInitialDataFilter={setSelectedfilteredInitialDataFilter} selectedfilteredOutcomeDataFilter={selectedfilteredOutcomeDataFilter} setSelectedfilteredOutcomeDataFilter={setSelectedfilteredOutcomeDataFilter} openDatePicker={openDatePicker[index] || false}   setOpenDatePicker={() => handleOpenDatePicker(index)}/>
        </div>
      ))}
    </Suspense>
    {isLoading  &&  <div className='loadergif'><img src={loaderGif} alt='loader' /> </div> }
      </Container> 
    </Box>
  );
};

export default ReportCharts;


